import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PlusCircle, Trash2, RotateCcw, Newspaper, Calendar, Image, FileText, Pencil, Eye, EyeOff, Paperclip, Rss, Search, Power, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';

const BACKEND_URL = '';

const NEWS_CATEGORIES = [
  "Sustainability", "Logistics", "Supply Chain", "Net Zero", "Circular Economy",
  "Green Technology", "Policy & Regulation", "Industry News", "Community Update",
  "Research & Reports", "Events", "Other"
];

export function AdminNewsTab() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    title: '', summary: '', content: '', category: '', status: 'published',
    external_url: '', is_sll_member_news: false, is_featured: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [docFiles, setDocFiles] = useState([]);

  useEffect(() => { loadStories(); }, []);

  const loadStories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/admin-list`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStories(data.stories || []);
      }
    } catch {}
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ title: '', summary: '', content: '', category: '', status: 'published', external_url: '', is_sll_member_news: false, is_featured: false });
    setImageFile(null);
    setImagePreview('');
    setDocFiles([]);
    setEditingStory(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleDocChange = (e) => {
    const files = Array.from(e.target.files || []);
    setDocFiles(prev => [...prev, ...files].slice(0, 5));
  };

  const removeDoc = (idx) => {
    setDocFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.summary.trim()) { toast.error('Summary is required'); return; }
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('summary', form.summary);
      formData.append('content', form.content);
      formData.append('category', form.category);
      formData.append('status', form.status);
      formData.append('external_url', form.external_url || '');
      formData.append('is_sll_member_news', form.is_sll_member_news ? 'true' : 'false');
      formData.append('is_featured', form.is_featured ? 'true' : 'false');

      if (imageFile) formData.append('image', imageFile);
      docFiles.forEach((f, i) => formData.append(`doc_${i}`, f));

      const isEdit = !!editingStory;
      const url = isEdit
        ? `${BACKEND_URL}/api/news/${editingStory.news_id}`
        : `${BACKEND_URL}/api/news/create-story`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, { method, credentials: 'include', body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }

      toast.success(isEdit ? 'Story updated' : 'Story published');
      setShowForm(false);
      resetForm();
      loadStories();
    } catch (e) {
      toast.error(e.message);
    }
    setSubmitting(false);
  };

  const handleDelete = async (story) => {
    if (!(await window.appConfirm(`Delete "${story.title}"? This cannot be undone.`))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${story.news_id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      toast.success('Story deleted');
      loadStories();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = async (story) => {
    // Pre-open the dialog with whatever we have from the list view so the UI
    // feels instant, then hydrate `content` from the admin single endpoint.
    // The list endpoint strips `content` for performance, so without this
    // call the Full Content textarea would render empty on every edit.
    setEditingStory(story);
    setForm({
      title: story.title || '',
      summary: story.summary || '',
      content: story.content || '',
      category: story.category || '',
      status: story.status || 'published',
      external_url: story.external_url || story.link || '',
      is_sll_member_news: !!story.is_sll_member_news,
      is_featured: !!story.is_featured,
    });
    setImagePreview(story.image || '');
    setImageFile(null);
    setDocFiles([]);
    setShowForm(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/news/${story.news_id}`, { credentials: 'include' });
      if (res.ok) {
        const full = await res.json();
        setEditingStory(prev => prev && prev.news_id === story.news_id ? { ...prev, ...full } : prev);
        setForm(f => ({
          ...f,
          title: full.title ?? f.title,
          summary: full.summary ?? f.summary,
          content: full.content ?? f.content,
          category: full.category ?? f.category,
          status: full.status ?? f.status,
          external_url: full.external_url ?? full.link ?? f.external_url,
          is_sll_member_news: typeof full.is_sll_member_news === 'boolean' ? full.is_sll_member_news : f.is_sll_member_news,
          is_featured: typeof full.is_featured === 'boolean' ? full.is_featured : f.is_featured,
        }));
        if (full.image) setImagePreview(full.image);
      }
    } catch (e) {
      // Non-fatal — the user can still edit the metadata fields even if the
      // hydration call fails. They'll just see the empty `content` box.
    }
  };

  const adminStories = stories.filter(s => s.source_type === 'admin');
  const userSubmissions = stories.filter(s => s.source_type === 'user_submission');
  const pendingSubmissions = userSubmissions.filter(s => s.status === 'pending_review');
  const reviewedSubmissions = userSubmissions.filter(s => s.status !== 'pending_review');
  const rssStories = stories.filter(s => s.source_type !== 'admin' && s.source_type !== 'user_submission');

  const handleApprove = async (story) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${story.news_id}/approve`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      toast.success(`Approved — ${story.submitted_by_name || 'submitter'} has been notified`);
      loadStories();
    } catch (e) { toast.error(e.message || 'Failed to approve'); }
  };

  const handleReject = async (story) => {
    const reason = window.prompt(
      `Reject "${story.title}"?\n\nOptionally add a short note to send to ${story.submitted_by_name || 'the submitter'} (leave blank to skip):`,
      '',
    );
    if (reason === null) return; // cancelled
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${story.news_id}/reject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: (reason || '').trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      toast.success(`Rejected — ${story.submitted_by_name || 'submitter'} has been notified`);
      loadStories();
    } catch (e) { toast.error(e.message || 'Failed to reject'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-news-content">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl font-bold font-heading">News Management</h2>
          <p className="text-sm text-slate-500 mt-0.5">Create and manage news stories. Admin stories appear on the News page with a featured badge.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={loadStories} data-testid="refresh-news-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="create-news-btn">
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> Create Story
          </Button>
        </div>
      </div>

      {/* Pending member submissions — review-required workflow */}
      {pendingSubmissions.length > 0 && (
        <div data-testid="admin-pending-submissions">
          <h3 className="text-sm font-bold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            Pending Submissions ({pendingSubmissions.length})
          </h3>
          <div className="space-y-2">
            {pendingSubmissions.map(story => {
              const link = story.external_url || story.link;
              const att = (story.attachments || [])[0];
              return (
                <Card
                  key={story.news_id}
                  className="p-4 border-2 border-amber-200 bg-gradient-to-br from-amber-50/40 to-white"
                  data-testid={`pending-submission-${story.news_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-amber-200 text-amber-900">
                          AWAITING REVIEW
                        </span>
                        {story.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {story.category}
                          </span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                          ✓ Permission confirmed
                        </span>
                      </div>
                      <p className="font-semibold text-sm text-slate-800 mb-0.5">{story.title}</p>
                      <p className="text-xs text-slate-600 line-clamp-2 mb-2">{story.summary}</p>
                      <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
                        <span><Calendar className="h-3 w-3 inline mr-0.5" />
                          {new Date(story.submitted_at || story.published_at).toLocaleDateString()}
                        </span>
                        <span>by <strong className="text-slate-700">{story.submitted_by_name || 'Member'}</strong></span>
                        {story.submitted_by_email && (
                          <span className="text-slate-400 truncate">{story.submitted_by_email}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap mt-2">
                        {link && (
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-brand-navy hover:underline inline-flex items-center gap-1"
                            data-testid={`submission-url-${story.news_id}`}
                          >
                            <ExternalLink className="h-3 w-3" /> View source URL
                          </a>
                        )}
                        {att && (
                          <a
                            href={`${BACKEND_URL}/api/news/attachments/${att.attachment_id}/download`}
                            className="text-xs text-brand-navy hover:underline inline-flex items-center gap-1"
                            data-testid={`submission-pdf-${story.news_id}`}
                          >
                            <Paperclip className="h-3 w-3" /> {att.filename}
                            <span className="text-slate-400 ml-1">({(att.size / 1024).toFixed(0)} KB)</span>
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(story)}
                        data-testid={`approve-submission-${story.news_id}`}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(story)}
                        data-testid={`reject-submission-${story.news_id}`}
                        className="border-rose-200 text-rose-600 hover:bg-rose-50"
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Reviewed member submissions (approved/rejected) — collapsed history */}
      {reviewedSubmissions.length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-white" data-testid="admin-reviewed-submissions">
          <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700 uppercase tracking-wide select-none">
            Reviewed Member Submissions ({reviewedSubmissions.length})
          </summary>
          <div className="px-4 pb-3 space-y-2">
            {reviewedSubmissions.map(story => (
              <div key={story.news_id} className="flex items-center gap-3 text-sm py-1.5 border-t border-slate-100">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  story.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'
                }`}>
                  {story.status === 'published' ? 'APPROVED' : 'REJECTED'}
                </span>
                <span className="font-medium text-slate-800 truncate flex-1">{story.title}</span>
                <span className="text-xs text-slate-400 whitespace-nowrap">by {story.submitted_by_name || 'Member'}</span>
                {story.status === 'published' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 h-7 w-7 p-0"
                    onClick={() => handleDelete(story)}
                    data-testid={`delete-reviewed-${story.news_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Admin-created stories */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
          Your Stories ({adminStories.length})
        </h3>
        {adminStories.length === 0 ? (
          <Card className="p-8 text-center">
            <Newspaper className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No admin stories yet. Click "Create Story" to publish one.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {adminStories.map(story => (
              <Card key={story.news_id} className="p-4" data-testid={`admin-story-${story.news_id}`}>
                <div className="flex items-center gap-4">
                  {story.image && (
                    <img src={story.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="font-semibold text-sm text-slate-800 truncate">{story.title}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                        story.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                      }`}>{story.status === 'draft' ? 'DRAFT' : 'PUBLISHED'}</span>
                      {story.category && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{story.category}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-1">{story.summary}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      <span><Calendar className="h-3 w-3 inline mr-0.5" />{new Date(story.published_at).toLocaleDateString()}</span>
                      {story.attachments?.length > 0 && (
                        <span><Paperclip className="h-3 w-3 inline mr-0.5" />{story.attachments.length} doc{story.attachments.length > 1 ? 's' : ''}</span>
                      )}
                      <span>
                        {story.status === 'draft' ? <EyeOff className="h-3 w-3 inline mr-0.5" /> : <Eye className="h-3 w-3 inline mr-0.5" />}
                        {story.thumbs_up || 0} likes
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(story)} data-testid={`edit-story-${story.news_id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(story)} data-testid={`delete-story-${story.news_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* RSS Management */}
      <RSSManagementSection onFetchTriggered={loadStories} />

      {/* Orphaned RSS sources */}
      <OrphanedRSSSection onChanged={loadStories} />

      {/* RSS stories count */}
      <div className="pt-2">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
          RSS Feed Stories ({rssStories.length})
        </h3>
        <Card className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-slate-600">
              {rssStories.length} stories from RSS feeds — refreshed automatically every 30 minutes in the background.
            </p>
            <Button size="sm" variant="outline" onClick={async () => {
              try {
                const res = await fetch(`${BACKEND_URL}/api/news/fetch-rss`, { method: 'POST', credentials: 'include' });
                if (res.ok) {
                  const data = await res.json();
                  toast.success(data.message || 'Fetched');
                  loadStories();
                } else { toast.error('Fetch failed'); }
              } catch { toast.error('Failed to fetch RSS'); }
            }} data-testid="fetch-rss-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Fetch RSS Now
            </Button>
          </div>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="news-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingStory ? 'Edit Story' : 'Create News Story'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Enter headline..." data-testid="news-title-input" />
            </div>
            <div>
              <Label>Summary *</Label>
              <RichTextEditor value={form.summary} onChange={v => setForm(f => ({ ...f, summary: v }))}
                placeholder="Brief description shown on the card..." testId="news-summary-input" minHeight="5rem" />
            </div>
            <div>
              <Label>Full Content</Label>
              <RichTextEditor value={form.content} onChange={v => setForm(f => ({ ...f, content: v }))}
                placeholder="Write the full story here..." testId="news-content-input" minHeight="12rem" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="news-category-select">
                  <option value="">Select category</option>
                  {NEWS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} data-testid="news-status-select">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> External Article URL (optional)</Label>
              <Input value={form.external_url} onChange={e => setForm(f => ({ ...f, external_url: e.target.value }))}
                placeholder="https://example.com/whitepaper.pdf"
                data-testid="news-external-url-input" />
              <p className="text-[11px] text-slate-400 mt-1">Members will see an "Additional information" button in the article modal pointing to this URL (e.g., a partner's white paper or full report).</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-brand-navy/5 border border-brand-navy/15">
              <input type="checkbox" id="is-sll-member-news"
                checked={form.is_sll_member_news}
                onChange={e => setForm(f => ({ ...f, is_sll_member_news: e.target.checked }))}
                data-testid="news-sll-member-checkbox"
                className="h-4 w-4 rounded border-slate-300 accent-brand-navy" />
              <Label htmlFor="is-sll-member-news" className="text-sm cursor-pointer flex-1 m-0">
                Mark as <span className="font-bold text-brand-navy">SLL Member News</span>
                <span className="block text-[11px] text-slate-500 font-normal mt-0.5">Highlights this article as community-driven and makes it filterable by all members.</span>
              </Label>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-brand-sage/5 border border-brand-sage/25">
              <input type="checkbox" id="is-featured"
                checked={form.is_featured}
                onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))}
                data-testid="news-featured-checkbox"
                className="h-4 w-4 rounded border-slate-300 accent-brand-sage" />
              <Label htmlFor="is-featured" className="text-sm cursor-pointer flex-1 m-0">
                Mark as <span className="font-bold text-brand-sage">Featured</span>
                <span className="block text-[11px] text-slate-500 font-normal mt-0.5">Adds the green "Featured" pill on the article card and surfaces it under the Featured filter.</span>
              </Label>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Image className="h-3.5 w-3.5" /> Featured Image</Label>
              <Input type="file" accept="image/*" onChange={handleImageChange} data-testid="news-image-input" />
              {imagePreview && (
                <img src={imagePreview} alt="Preview" className="mt-2 w-full h-40 object-cover rounded-lg border" />
              )}
            </div>
            {!editingStory && (
              <div>
                <Label className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Document Attachments (up to 5)</Label>
                <Input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  onChange={handleDocChange} data-testid="news-docs-input" />
                {docFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {docFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded px-3 py-1.5 border">
                        <span className="truncate flex-1"><Paperclip className="h-3 w-3 inline mr-1" />{f.name}</span>
                        <span className="text-slate-400 mr-2">{(f.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => removeDoc(i)} className="text-red-400 hover:text-red-600 text-xs font-bold">X</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button onClick={handleSubmit} disabled={submitting}
              className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="save-news-btn">
              {submitting ? 'Saving...' : editingStory ? 'Update Story' : 'Publish Story'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function OrphanedRSSSection({ onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busySource, setBusySource] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/news/orphaned-rss`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deleteBySource = async (source, count) => {
    if (!(await window.appConfirm(`Permanently delete all ${count} article(s) sourced from "${source}"? The feed for this source is not active.`))) return;
    setBusySource(source);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/news/cleanup-orphans`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Removed ${d.deleted} article(s) from "${source}".`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message || 'Failed'); }
    setBusySource(null);
  };

  const deleteAllOrphans = async (total) => {
    if (!(await window.appConfirm(`Permanently delete all ${total} orphaned article(s) from sources that no longer have an active RSS feed?`))) return;
    setBusySource('__all__');
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/news/cleanup-orphans`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ all: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Removed ${d.deleted} orphaned article(s).`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message || 'Failed'); }
    setBusySource(null);
  };

  const orphans = (data?.sources || []).filter(s => !s.matches_active_feed);

  return (
    <div className="pt-2">
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">
        Orphaned RSS Sources ({orphans.length})
      </h3>
      <Card className="p-4">
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : orphans.length === 0 ? (
          <p className="text-sm text-slate-500">No orphans. Every RSS article in the database belongs to an active feed.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <p className="text-sm text-slate-600">
                These articles came from RSS feeds that have since been removed. Delete them to clean up the public News page.
              </p>
              <Button
                size="sm"
                onClick={() => deleteAllOrphans(data.orphan_total)}
                disabled={busySource === '__all__'}
                data-testid="cleanup-all-orphans-btn"
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete all {data.orphan_total} orphans
              </Button>
            </div>
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
              {orphans.map(s => (
                <div key={s.source} className="flex items-center justify-between px-3 py-2.5 hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-700 truncate" data-testid={`orphan-source-${s.source}`}>{s.source}</p>
                    <p className="text-xs text-slate-400">{s.count} article{s.count === 1 ? '' : 's'}{s.feed_urls.length > 0 ? ` · last feed: ${s.feed_urls[0]}` : ''}</p>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => deleteBySource(s.source, s.count)}
                    disabled={busySource === s.source}
                    data-testid={`delete-orphan-${s.source}`}
                    className="text-rose-600 hover:text-rose-700 border-rose-200 hover:border-rose-300"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


function RSSManagementSection({ onFetchTriggered }) {
  const [feeds, setFeeds] = useState([]);
  const [detected, setDetected] = useState({ sources: [], orphan_source_count: 0, orphan_total: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newName, setNewName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rFeeds, rDetected] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/rss-feeds`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/rss-feeds/detected-sources`, { credentials: 'include' }),
      ]);
      if (rFeeds.ok) setFeeds(await rFeeds.json());
      if (rDetected.ok) setDetected(await rDetected.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/rss-feeds/reconcile`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Reconcile failed');
      const parts = [];
      if (data.created_count) parts.push(`Added ${data.created_count} feed${data.created_count === 1 ? '' : 's'} from existing news`);
      if (data.missing_url_count) parts.push(`${data.missing_url_count} source${data.missing_url_count === 1 ? '' : 's'} still need a URL — add manually below`);
      toast.success(parts.length ? parts.join(' · ') : 'Nothing to reconcile');
      load();
    } catch (e) {
      toast.error(e.message || 'Reconcile failed');
    }
    setReconciling(false);
  };

  const handleAddFromDetected = (source) => {
    setNewName(source);
    setNewUrl('');
    setPreviewResult(null);
    setShowAdd(true);
  };

  const handlePreview = async (url) => {
    if (!url?.trim()) return;
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/rss-feeds/preview`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Preview failed');
      setPreviewResult(data);
      if (!newName && data.feed_title) setNewName(data.feed_title);
    } catch (e) {
      toast.error(e.message || 'Preview failed');
    }
    setPreviewing(false);
  };

  const handleAdd = async () => {
    if (!newUrl.trim()) { toast.error('Enter a feed URL'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/rss-feeds`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: newUrl.trim(), name: newName.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Add failed');
      toast.success(`Added "${data.name}"`);
      setShowAdd(false);
      setNewUrl(''); setNewName(''); setPreviewResult(null); setPreviewUrl('');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to add feed');
    }
    setSubmitting(false);
  };

  const handleToggle = async (feed) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/rss-feeds/${feed.feed_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !feed.enabled }),
      });
      if (r.ok) {
        toast.success(feed.enabled ? 'Disabled' : 'Enabled');
        load();
      }
    } catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (feed) => {
    const ok = window.appConfirm
      ? await window.appConfirm(`Remove "${feed.name}" from RSS rotation? Existing stories already imported will remain.`)
      : window.confirm(`Remove "${feed.name}"?`);
    if (!ok) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/rss-feeds/${feed.feed_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) { toast.success('Removed'); load(); }
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="pt-2" data-testid="rss-management-section">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
          <Rss className="h-4 w-4 text-orange-500" /> RSS Feeds ({feeds.length})
        </h3>
        <div className="flex items-center gap-1.5">
          {detected.orphan_source_count > 0 && (
            <Button size="sm" variant="outline" onClick={handleReconcile} disabled={reconciling}
              data-testid="reconcile-feeds-btn" className="text-brand-navy border-brand-navy/30 hover:bg-brand-navy/5">
              <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${reconciling ? 'animate-spin' : ''}`} />
              {reconciling ? 'Reconciling…' : `Reconcile (${detected.orphan_source_count})`}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowAdd(true)} data-testid="add-rss-feed-btn">
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> Add Feed
          </Button>
        </div>
      </div>
      <Card className="p-3">
        {loading ? (
          <p className="text-sm text-slate-400 py-3 text-center">Loading…</p>
        ) : feeds.length === 0 ? (
          <div className="py-3 text-center space-y-2">
            <p className="text-sm text-slate-500">
              <strong className="text-slate-700">No RSS feeds tracked yet.</strong>
            </p>
            <p className="text-xs text-slate-400 max-w-lg mx-auto">
              {detected.orphan_source_count > 0 ? (
                <>We've detected <strong>{detected.orphan_source_count} source{detected.orphan_source_count === 1 ? '' : 's'}</strong> ({detected.orphan_total} article{detected.orphan_total === 1 ? '' : 's'}) already in your news feed. Press <em>Reconcile</em> to auto-add every source that has a known URL, then use <em>Add Feed</em> for the rest.</>
              ) : (
                <>Add a feed to start managing your RSS rotation. Pressing <em>Fetch RSS Now</em> without any feeds will seed a small default sustainability set that you can then edit or remove.</>
              )}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {feeds.map(f => (
              <div key={f.feed_id} className="flex items-center gap-2 py-2.5" data-testid={`rss-feed-row-${f.feed_id}`}>
                <Rss className={`h-4 w-4 flex-shrink-0 ${f.enabled ? 'text-orange-500' : 'text-slate-300'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${f.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                    {f.name || f.url}
                    {f.auto_reconciled && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wide">Auto-detected</span>
                    )}
                    {f.seeded_default && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wide">Default</span>
                    )}
                  </p>
                  <a href={f.url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-slate-400 hover:text-brand-navy hover:underline truncate flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> {f.url}
                  </a>
                  {f.last_fetched_at && (
                    <p className="text-[10px] text-slate-300 mt-0.5">Last fetched {new Date(f.last_fetched_at).toLocaleString()}</p>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => handlePreview(f.url)} title="Preview"
                  data-testid={`preview-feed-${f.feed_id}`}>
                  <Search className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleToggle(f)}
                  title={f.enabled ? 'Disable' : 'Enable'} data-testid={`toggle-feed-${f.feed_id}`}>
                  <Power className={`h-3.5 w-3.5 ${f.enabled ? 'text-green-600' : 'text-slate-400'}`} />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                  onClick={() => handleDelete(f)} data-testid={`delete-feed-${f.feed_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Detected sources (from articles) that don't yet have a feed entry.
          Always render this callout when we have unmanaged sources — even
          when there are already active feeds — so admins can see leftover
          orphans from removed feeds and act on them. */}
      {!loading && detected.orphan_source_count > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="detected-sources-panel">
          <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {detected.orphan_source_count} detected source{detected.orphan_source_count === 1 ? '' : 's'} without an active feed
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                These sources have already produced {detected.orphan_total} article{detected.orphan_total === 1 ? '' : 's'}. Reconcile to auto-track feeds we have a URL for, or add manually.
              </p>
            </div>
            <Button size="sm" onClick={handleReconcile} disabled={reconciling}
              className="bg-brand-navy text-white hover:bg-brand-navy/90" data-testid="reconcile-feeds-btn-inline">
              <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${reconciling ? 'animate-spin' : ''}`} />
              {reconciling ? 'Reconciling…' : 'Reconcile now'}
            </Button>
          </div>
          <div className="divide-y divide-amber-100 bg-white/60 rounded border border-amber-100 max-h-60 overflow-y-auto">
            {detected.sources.filter(s => !s.matches_active_feed).map(s => (
              <div key={s.source} className="flex items-center gap-2 px-2.5 py-2" data-testid={`detected-source-${s.source}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{s.source}</p>
                  <p className="text-[11px] text-slate-500">
                    {s.count} article{s.count === 1 ? '' : 's'}
                    {s.feed_urls.length > 0 ? ` · ${s.feed_urls[0]}` : ' · no feed URL recorded'}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleAddFromDetected(s.source)}
                  data-testid={`add-detected-${s.source}`} className="text-xs">
                  Add feed URL
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add feed dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-rss-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Rss className="h-5 w-5 text-orange-500" /> Add RSS Feed</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Feed URL *</Label>
              <div className="flex gap-2">
                <Input value={newUrl} onChange={e => { setNewUrl(e.target.value); setPreviewResult(null); }}
                  placeholder="https://example.com/feed.xml" data-testid="rss-url-input" />
                <Button type="button" variant="outline" onClick={() => { setPreviewUrl(newUrl); handlePreview(newUrl); }}
                  disabled={previewing || !newUrl.trim()} data-testid="rss-preview-btn">
                  <Search className="h-4 w-4 mr-1.5" /> {previewing ? 'Loading…' : 'Preview'}
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Paste an RSS or Atom feed URL. Preview lets you check the feed before adding it to the rotation.</p>
            </div>

            <div>
              <Label>Display Name (optional)</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="e.g., The Guardian — Environment" data-testid="rss-name-input" />
            </div>

            {previewResult && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="rss-preview-result">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700">
                    Preview: <span className="text-brand-navy">{previewResult.feed_title || previewUrl}</span>
                  </p>
                  <span className="text-xs text-slate-500">{previewResult.item_count} items</span>
                </div>
                {previewResult.items?.length > 0 ? (
                  <ul className="space-y-2">
                    {previewResult.items.slice(0, 5).map((it, idx) => (
                      <li key={idx} className="flex gap-2 text-sm">
                        <span className="text-slate-400 text-xs flex-shrink-0">#{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-700 truncate">{it.title}</p>
                          {it.summary && <p className="text-xs text-slate-500 line-clamp-2">{it.summary}</p>}
                          {it.published && <p className="text-[10px] text-slate-400 mt-0.5">{it.published}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-rose-500">No items found at this URL. Double-check the address.</p>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => { setShowAdd(false); setNewUrl(''); setNewName(''); setPreviewResult(null); }}>
                Cancel
              </Button>
              <Button onClick={handleAdd} disabled={submitting || !newUrl.trim()}
                className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="rss-add-confirm">
                {submitting ? 'Adding…' : 'Add Feed'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview-only popover (when previewing existing feed) */}
      {previewResult && !showAdd && (
        <Dialog open={!!previewResult} onOpenChange={(o) => { if (!o) setPreviewResult(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Feed Preview — {previewResult.feed_title || ''}</DialogTitle></DialogHeader>
            <ul className="space-y-2">
              {previewResult.items?.map((it, idx) => (
                <li key={idx} className="border-b border-slate-100 pb-2">
                  <p className="font-medium text-slate-700">{it.title}</p>
                  {it.summary && <p className="text-sm text-slate-500">{it.summary}</p>}
                  {it.link && (
                    <a href={it.link} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-brand-navy hover:underline">{it.link}</a>
                  )}
                </li>
              ))}
            </ul>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
